import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, Settings as SettingsIcon, Mail, KanbanSquare, Network, ShieldCheck, Star, RefreshCcwDot, Users, LogOut, ChevronDown, Landmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef, useEffect } from "react";

const NavLink = ({ to, label, icon: Icon, testid }) => {
  const { pathname } = useLocation();
  const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-2 ${
        active
          ? "bg-[#14304F] text-white border-[#14304F]"
          : "bg-white text-[#14304F] border-transparent hover:border-[#14304F]"
      }`}
    >
      <Icon size={16} strokeWidth={2.5} />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  if (!user) return null;
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-menu-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 border-2 border-transparent hover:border-[#14304F] transition-colors"
      >
        <div className="h-7 w-7 bg-[#14304F] text-white flex items-center justify-center font-display font-bold text-xs">
          {(user.name || user.email)[0]?.toUpperCase()}
        </div>
        <span className="hidden md:inline text-sm font-bold text-[#14304F] max-w-[120px] truncate">{user.name || user.email}</span>
        <ChevronDown size={14} className="text-zinc-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border-2 border-[#14304F] shadow-[4px_4px_0px_0px_#14304F] z-50" data-testid="user-menu">
          <div className="px-4 py-3 border-b-2 border-zinc-100">
            <div className="text-sm font-bold truncate">{user.name}</div>
            <div className="text-xs text-zinc-500 font-mono truncate">{user.email}</div>
            {user.team?.name && <div className="text-[10px] font-mono uppercase tracking-wider text-[#2F6DA3] mt-1">{user.team.name}</div>}
          </div>
          <Link to="/team" onClick={() => setOpen(false)} data-testid="menu-team" className="flex items-center gap-2 px-4 py-3 text-sm font-bold hover:bg-zinc-50 transition-colors">
            <Users size={15} /> Team
          </Link>
          <button onClick={logout} data-testid="logout-button" className="w-full flex items-center gap-2 px-4 py-3 text-sm font-bold text-red-700 hover:bg-red-50 transition-colors border-t border-zinc-100">
            <LogOut size={15} /> Log out
          </button>
        </div>
      )}
    </div>
  );
};

const CustomerNav = () => (
  <>
    <NavLink to="/" label="Opportunities" icon={LayoutGrid} testid="nav-dashboard" />
    <NavLink to="/pipeline" label="Pipeline" icon={KanbanSquare} testid="nav-pipeline" />
    <NavLink to="/renewals" label="Renewals" icon={RefreshCcwDot} testid="nav-renewals" />
    <NavLink to="/favourites" label="Favourites" icon={Star} testid="nav-favourites" />
    <NavLink to="/frameworks" label="Frameworks" icon={Network} testid="nav-frameworks" />
    <NavLink to="/readiness" label="Readiness" icon={ShieldCheck} testid="nav-readiness" />
    <NavLink to="/digest" label="Weekly Email" icon={Mail} testid="nav-digest" />
    <NavLink to="/settings" label="Settings" icon={SettingsIcon} testid="nav-settings" />
  </>
);

export const Layout = ({ children }) => {
  const { user } = useAuth();
  const isSA = user?.platform_role === "super_admin";
  return (
    <div className="min-h-screen bg-zinc-100">
      <nav className="border-b-2 border-[#14304F] bg-white px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-50">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <img src="/cph-mark.png" alt="CPH" className="h-11 w-11 object-contain" />
          <div className="leading-none border-l-2 border-zinc-200 pl-3">
            <div className="font-display font-extrabold text-lg tracking-tight text-[#14304F]">CPH</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2F6DA3] font-bold">Contract Intelligence</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {isSA ? <NavLink to="/governance" label="Governance" icon={Landmark} testid="nav-governance" /> : <CustomerNav />}
          <div className="h-6 w-px bg-zinc-200 mx-1" />
          <UserMenu />
        </div>
      </nav>
      <main className="p-4 md:p-8 max-w-[1500px] mx-auto">{children}</main>
    </div>
  );
};
